// src/hooks/useTrialStatus.js
import { useState, useEffect } from 'react';
import useAuthToken from './useAuthToken';
import { useTrialDemo } from '../components/TrialWarningLiveDemo';
import { getApiBaseUrl } from '../utils/apiConfig';

const useTrialStatus = () => {
  const [trialStatus, setTrialStatus] = useState({
    loading: true,
    isTrialing: false,
    daysRemaining: null,
    trialEndDate: null,
    isExpired: false,
    isRestricted: false,
    error: null
  });
  
  const token = useAuthToken();
  const { demoMode, selectedState, mockTrialStates } = useTrialDemo();

  useEffect(() => {
    // Return demo data if demo mode is enabled - handle inside useEffect
    if (demoMode && mockTrialStates[selectedState]) {
      setTrialStatus(mockTrialStates[selectedState]);
      return;
    }
    const fetchTrialStatus = async () => {
      if (!token) {
        setTrialStatus(prev => ({ ...prev, loading: false }));
        return;
      }

      try {
        const response = await fetch(`${getApiBaseUrl()}/trial/status`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        
        setTrialStatus({
          loading: false,
          isTrialing: data.subscription_status === 'trialing',
          daysRemaining: data.days_remaining,
          trialEndDate: data.trial_end_date ? new Date(data.trial_end_date) : null,
          isExpired: data.is_expired || false,
          isRestricted: data.account_restricted || false,
          subscriptionStatus: data.subscription_status,
          error: null
        });

      } catch (error) {
        console.warn('Failed to fetch trial status:', error);
        
        // Fallback: Try to get info from JWT token
        try {
          const base64Url = token.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          }).join(''));
          const decoded = JSON.parse(jsonPayload);
          
          const subscriptionStatus = decoded.subscription_status || 'active';
          const isTrialing = subscriptionStatus === 'trialing';
          
          setTrialStatus({
            loading: false,
            isTrialing,
            daysRemaining: null, // Can't determine from JWT
            trialEndDate: null,
            isExpired: subscriptionStatus === 'expired',
            isRestricted: decoded.account_restricted || false,
            subscriptionStatus,
            error: error.message
          });
          
        } catch (jwtError) {
          console.error('Failed to decode JWT for trial status:', jwtError);
          setTrialStatus({
            loading: false,
            isTrialing: false,
            daysRemaining: null,
            trialEndDate: null,
            isExpired: false,
            isRestricted: false,
            subscriptionStatus: 'active',
            error: 'Unable to determine trial status'
          });
        }
      }
    };

    fetchTrialStatus();
    
    // Refresh trial status every 30 minutes
    const interval = setInterval(fetchTrialStatus, 30 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [token, demoMode, selectedState, mockTrialStates]);

  return trialStatus;
};

export default useTrialStatus;